import {
  ConfirmModal,
  Flex,
  Loading,
  Table,
  TableRowMenuCell,
  Text,
} from '@/components';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { useProjectPermission } from '@/features/authentication/_hooks/permission';
import { useMinistries } from '@/features/master-table/hooks';
import { getMinistryName } from '@/features/mold/utils';
import { useProject } from '@/features/project/_hooks/project';
import type { PaymentEdgeModel } from '@/features/project/projects/_models/payment-edge';
import {
  useDeletePaymentEdge,
  usePaymentEdges,
} from '@/features/project/projects/detail/payment/_hooks/payment-edge';
import { usePaymentGroups } from '@/features/project/projects/detail/payment/_hooks/payment-group';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { TABLE_DOUBLE_ICONS_WIDTH } from '@/utils';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';

export const PaymentEdgesTable: FC = () => {
  const { t } = useTranslation();
  const { id: projectId } = useId();
  const { openMessage } = useMessage();
  const { setDrawerState } = useProjectDetailDrawerState();
  const [paymentEdgeForDelete, setPaymentEdgeForDelete] = useState<z.infer<
    typeof PaymentEdgeModel
  > | null>(null);
  const paymentGroups = usePaymentGroups({ projectId });
  const paymentEdges = usePaymentEdges(projectId);
  const ministries = useMinistries();
  const project = useProject(projectId);
  const { mutateAsync: deletePaymentEdgeAsync } = useDeletePaymentEdge();
  const editPermission = useProjectPermission(true);

  if (
    paymentEdges.isPending ||
    !paymentEdges.data ||
    paymentGroups.isPending ||
    !paymentGroups.data
  ) {
    return <Loading />;
  }
  const ministryName = getMinistryName({
    targetId: project.data?.ministryId,
    ministries: ministries.data,
  });

  const handleClosePaymentEdgeForDeleteConfirmModal = async () => {
    setPaymentEdgeForDelete(null);
  };

  return (
    <Flex vertical gap="small">
      <Text variant="h3" text={t('payment-edge-list')} />
      <Table
        rowKey="id"
        dataSource={paymentEdges.data}
        columns={[
          {
            title: <Text text={t('source-payment-node')} />,
            key: 'sourceNodeId',
            dataIndex: 'sourceNodeId',
            render: (_, record) => {
              if (record.isConnectedToSourceRoot) {
                return ministryName;
              }
              const paymentGroup = paymentGroups.data?.find(
                (pg) => pg.id === record.sourceNodeId,
              );
              if (!paymentGroup) {
                return null;
              }
              return <>{`${paymentGroup.displayCode}. ${paymentGroup.name}`}</>;
            },
          },
          {
            title: <Text text={t('target-payment-node')} />,
            key: 'targetNodeId',
            dataIndex: 'targetNodeId',
            render: (_, record) => {
              const paymentGroup = paymentGroups.data?.find(
                (pg) => pg.id === record.targetNodeId,
              );
              if (!paymentGroup) {
                return null;
              }
              return <>{`${paymentGroup.displayCode}. ${paymentGroup.name}`}</>;
            },
          },
          ...(project.data && !project.data.isLocked && editPermission.data
            ? [
                {
                  title: '',
                  key: 'actions',
                  width: TABLE_DOUBLE_ICONS_WIDTH,
                  render: (_, record) => (
                    <PublishedProjectMutableGuard>
                      <Flex justify="end">
                        <TableRowMenuCell
                          onEdit={() => {
                            setDrawerState({
                              open: true,
                              type: 'payment-edge',
                              isEditing: true,
                              paymentEdgeId: record.id,
                            });
                          }}
                          items={[
                            {
                              key: 'delete',
                              label: t('delete'),
                              danger: true,
                              onClick: () => {
                                setPaymentEdgeForDelete(record);
                              },
                            },
                          ]}
                        />
                      </Flex>
                    </PublishedProjectMutableGuard>
                  ),
                },
              ]
            : []),
        ]}
        pagination={false}
        style={{ marginBottom: 16 }}
      />

      <ConfirmModal
        open={paymentEdgeForDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(paymentEdgeForDelete, t('unreachable-code'));
          try {
            await deletePaymentEdgeAsync({
              id: paymentEdgeForDelete.id,
              projectId: paymentEdgeForDelete.projectId,
            });
            await Promise.all([
              openMessage('success', t('message-delete-completed')),
              handleClosePaymentEdgeForDeleteConfirmModal(),
            ]);
          } catch {
            await openMessage('error', t('message-delete-failed'));
          }
        }}
        onCancel={handleClosePaymentEdgeForDeleteConfirmModal}
        skipCheck
      />
    </Flex>
  );
};
